import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';

/**
 * Wraps the ApartmentSync interactive map block and recovers when Repli360
 * leaves a perpetual loading spinner (empty SightMap `i_url`).
 *
 * ApartmentSync injects a `.loadingImage` into `.rrac_interactive_leasing_view`
 * and only replaces it when the sitemap API returns an iframe URL. For this
 * property that URL is empty, so without this guard the page shows a spinner
 * forever.
 */
type MapStatus = 'pending' | 'ready' | 'unavailable';

const STUCK_MS = 8000;

export function InteractiveMapSection() {
  const blockSlot = useBlockSlot('interactivemapsection.block', {
    defaultValue: 'apartment-sync-map',
  });
  const unavailableMessage = useContentToken('interactivemapsection.unavailable_message');
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<MapStatus>('pending');

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    let settled = false;

    const markReady = () => {
      if (settled) return;
      if (!root.querySelector('iframe')) return;
      settled = true;
      setStatus('ready');
    };

    const markUnavailable = () => {
      if (settled) return;
      const hasIframe = !!root.querySelector('iframe');
      const hasLoader = !!root.querySelector('.loadingImage, .rrac_loader_container');
      // Empty container with no iframe after the vendor had a chance to run
      // also counts as unavailable (script failed / no map configured).
      if (!hasIframe && (hasLoader || root.querySelector('.rrac_interactive_leasing_view'))) {
        settled = true;
        setStatus('unavailable');
      }
    };

    markReady();

    const observer = new MutationObserver(() => {
      markReady();
    });
    observer.observe(root, { childList: true, subtree: true });

    const timer = window.setTimeout(markUnavailable, STUCK_MS);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [blockSlot?.sectionId]);

  // Once we know the vendor map will never arrive, unmount the block so the
  // spinner (and any RRAC body helpers) are torn down cleanly.
  const showBlock = status !== 'unavailable';

  return (
    <section className="interactivemapsection bg-background">
      <EditableToken
        path="interactivemapsection.block"
        as="div"
        className="interactivemapsection__inner"
      >
        {showBlock && blockSlot ? (
          <div
            ref={containerRef}
            className={
              status === 'ready'
                ? 'interactivemapsection__map'
                : 'interactivemapsection__map interactivemapsection__map--pending'
            }
          >
            {status === 'pending' ? (
              <div className="interactivemapsection__skeleton" aria-hidden="true" />
            ) : null}
            <SectionContextProvider sectionId={blockSlot.sectionId}>
              <blockSlot.BlockComponent sectionId={blockSlot.sectionId} />
            </SectionContextProvider>
          </div>
        ) : null}

        <div
          className="interactivemapsection__fallback"
          hidden={status !== 'unavailable'}
        >
          <EditableToken
            path="interactivemapsection.unavailable_message"
            as="p"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)]"
          >
            {unavailableMessage}
          </EditableToken>
        </div>

        {!blockSlot && status !== 'unavailable' ? (
          <div className="interactivemapsection__empty">No map block selected</div>
        ) : null}
      </EditableToken>

      <style>{`
        .interactivemapsection {
          width: 100%;
        }
        .interactivemapsection__inner {
          width: 100%;
        }
        .interactivemapsection__map {
          position: relative;
          width: 100%;
          min-height: 280px;
        }
        .interactivemapsection__map--pending .rrac_interactive_leasing_view {
          /* Hide the vendor spinner while we wait; our skeleton stands in. */
          opacity: 0;
          min-height: 280px;
        }
        .interactivemapsection__skeleton {
          position: absolute;
          inset: 0;
          z-index: 1;
          min-height: 280px;
          background: linear-gradient(
            90deg,
            var(--vibe-color-surface, #f3f4f6) 0%,
            var(--vibe-color-background, #ffffff) 50%,
            var(--vibe-color-surface, #f3f4f6) 100%
          );
          background-size: 200% 100%;
          animation: interactivemapsection-shimmer 1.4s ease-in-out infinite;
        }
        @keyframes interactivemapsection-shimmer {
          0% { background-position: 100% 0; }
          100% { background-position: -100% 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .interactivemapsection__skeleton { animation: none; }
        }
        .interactivemapsection__fallback {
          max-width: 40rem;
          margin: 0 auto;
          padding: 2.5rem 1rem 3rem;
          text-align: center;
        }
        .interactivemapsection__empty {
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 2rem 1rem;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapSection',
  displayName: 'Interactive Map Section',
  tokens: {
    'interactivemapsection.block': {
      label: 'Map Block',
      type: 'block',
      defaultValue: 'apartment-sync-map',
      acceptedBlockCategories: ['Integrations', 'Embeds'],
    },
    'interactivemapsection.unavailable_message': {
      label: 'Unavailable Message',
      type: 'text',
      defaultValue:
        'The interactive community map is temporarily unavailable. Please check back soon or browse our floor plans.',
    },
  },
};

(InteractiveMapSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default InteractiveMapSection;
